import { z } from "zod";

// One typed, validated env object, parsed once at boot (.claude/rules/config.md; TASK.md "The
// client contracts"). A missing required field renders the honest boot-failure state naming
// it — never a blank page, never a silent default. `source` defaults to `import.meta.env` but
// is injectable so tests never touch the real one (apps/api's `loadConfig(env)`, mirrored).

/** Whatever shape the environment arrives in — Vite's real `import.meta.env` at runtime, or a
 * plain object a test hands in directly. Zod validates the values regardless of this type. */
export type EnvSource = Record<string, string | boolean | undefined>;

// Keys are environment variable names — an external SCREAMING_SNAKE_CASE contract (Vite's own
// convention), not ours to camelCase. Each needs its own biome-ignore: quoting the keys doesn't
// escape the wall (Biome's naming-convention rule checks quoted keys too, and the formatter
// un-quotes valid identifiers straight back on `--write`) and the rule isn't disableable from
// here (biome.json is outside apps/admin's ownership — CLAUDE.md).
const baseEnvSchema = z.object({
  // biome-ignore lint/style/useNamingConvention: VITE_API_URL is the environment variable name.
  VITE_API_URL: z
    .string()
    .min(1, "VITE_API_URL is required. The admin cannot reach the API without it."),
  // biome-ignore lint/style/useNamingConvention: VITE_CLERK_PUBLISHABLE_KEY is the environment variable name.
  VITE_CLERK_PUBLISHABLE_KEY: z
    .string()
    .min(1, "VITE_CLERK_PUBLISHABLE_KEY is required. Sign-in cannot render without it."),
  // An unset Vite env var arrives as "" (empty string), not undefined — .env.local sets
  // VITE_SENTRY_DSN="" in dev on purpose (STACK.md's "no DSN = disabled" convention).
  // Preprocess "" to undefined so `.optional()` actually treats it as absent rather than
  // failing `.min(1)`.
  // biome-ignore lint/style/useNamingConvention: VITE_SENTRY_DSN is the environment variable name.
  VITE_SENTRY_DSN: z.preprocess(
    (value) => (value === "" ? undefined : value),
    z.string().min(1).optional(),
  ),
  // biome-ignore lint/style/useNamingConvention: SENTRY_RELEASE is the environment variable name.
  SENTRY_RELEASE: z
    .string()
    .min(
      1,
      "SENTRY_RELEASE is required. It is injected at build (vite.config.ts), never left unset.",
    ),
  // biome-ignore lint/style/useNamingConvention: PROD is Vite's own built-in import.meta.env key.
  PROD: z.boolean(),
});

/** Production-only tightening — dev/test may leave the Sentry DSN unset. */
const validatedEnvSchema = baseEnvSchema.superRefine((env, ctx) => {
  if (env.PROD && !env.VITE_SENTRY_DSN) {
    ctx.addIssue({
      code: "custom",
      path: ["VITE_SENTRY_DSN"],
      message: "VITE_SENTRY_DSN is required in production builds. Errors must reach the tracker.",
    });
  }
});

export interface AdminEnv {
  apiUrl: string;
  clerkPublishableKey: string;
  sentryDsn: string | undefined;
  sentryRelease: string;
  isProd: boolean;
}

/** Carries the field names that failed so the boot-failure screen can name them exactly. */
export class EnvValidationError extends Error {
  readonly fields: string[];

  constructor(message: string, fields: string[]) {
    super(message);
    this.name = "EnvValidationError";
    this.fields = fields;
  }
}

function parseEnv(source: EnvSource): AdminEnv {
  const parsed = validatedEnvSchema.safeParse(source);
  if (!parsed.success) {
    const fields = parsed.error.issues.map((issue) => String(issue.path[0] ?? "(root)"));
    const detail = parsed.error.issues
      .map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`)
      .join("; ");
    throw new EnvValidationError(detail, fields);
  }
  return {
    apiUrl: parsed.data.VITE_API_URL,
    clerkPublishableKey: parsed.data.VITE_CLERK_PUBLISHABLE_KEY,
    sentryDsn: parsed.data.VITE_SENTRY_DSN,
    sentryRelease: parsed.data.SENTRY_RELEASE,
    isProd: parsed.data.PROD,
  };
}

// Cached on the real `import.meta.env` path only — a test that hands in its own `source` always
// gets a fresh parse, so the cache is invisible to the existing test suite. The cache exists so
// "read once" (.claude/rules/config.md) stays literally true now that two boundaries read it:
// main.tsx at boot, and the authenticated route (src/routes/_authenticated.tsx) — split out so
// the API client, and the packages/types schemas it pulls in, aren't in the /sign-in eager graph
// (perf-route-splitting). Both resolve the same already-validated object, no re-parse.
let cachedEnv: AdminEnv | undefined;

/**
 * Read and validate the environment. Throws (fail closed) with a field-by-field, value-free
 * message if anything required is absent or malformed — call at a boundary (main.tsx; the
 * authenticated route for the API client's baseUrl), and never read `import.meta.env` directly
 * anywhere else in the app.
 */
export function loadEnv(source?: EnvSource): AdminEnv {
  if (source !== undefined) {
    return parseEnv(source);
  }
  if (!cachedEnv) {
    cachedEnv = parseEnv(import.meta.env as EnvSource);
  }
  return cachedEnv;
}
